import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useLocation } from 'react-router-dom';
import { RemoveSpaceSlash, CheckIconBig, contactUSQuestionTypes, contactUSOptions, domainForSegment } from 'common';
import {
  Button,
  Label,
  Select,
  TextArea,
  ModalOverlayWithoutClose,
  Headings,
  Paragraph
} from '@sentara-health/optimahealth_web';
import { appName, GlobalContext } from 'context';
import { useAdobeAnalytics } from '@sentara-health/platform-analytics';
import { AdobeFormConfig } from 'common/AdobeFormConfig';


const ContactUs = () => {
  const {
    loginMemberId,
    profileState,
  } = useContext(GlobalContext);
  const contactUsBaseURL = `${window.location.href}?success=true`;
  const [searchParams, setSearchParams]: any = useSearchParams();
  const { formSubmissionSucceeded, formSubmissionFailed, pageView } = useAdobeAnalytics();
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const [selectedList, setSelectedList] = useState({
    questionTypeDropdown: '',
    howContactedDropdown: '',
    yourQuestion: '',
  });
  const [isOpen, setIsOpen] =
    useState(false); /** Default open value set to false*/

  type ContactUSConfig = {
  orgId: string;
  memberIdKey: string;
  questionTypeKey: string;
  contactOptionKey: string;
  url: string;
};
  const questionTypes = contactUSQuestionTypes;
  const contactOptions = contactUSOptions;
  const memberId = loginMemberId;
  const ContactUSKeyValues: ContactUSConfig = JSON.parse(
  process.env.REACT_APP_CONTACTUS_KEY_VALUES || "{}"
);
  const removeSpaceSlash = (textId: string) => {
    return textId
      .trim()
      .replace(RemoveSpaceSlash, '');
  };
  useEffect(() => {
      const params = searchParams.get('success');
     // setIsOpen(params === 'true');
      const isSuccess = params === 'true';
      setIsOpen(isSuccess);
      if (isSuccess) {
        formSubmissionSucceeded({
              segment: `${domainForSegment}${pathname}`,
              form: AdobeFormConfig['contactUsFormSuccess'],
            });
            setSearchParams({});
        }
  }, []);

  const handleQuestionChange = (e: { target: { name: any; value: any } }) => {
    const { name, value } = e.target;
    setSelectedList({ ...selectedList, [name]: value });
  };
  const redirectToContact = (e: Event) => {
    e.preventDefault();
    formSubmissionSucceeded({
          segment: `${domainForSegment}${pathname}`,
          form: AdobeFormConfig['contactUsFormSubmit'],
        });
    const form = document.getElementById('salesForce') as HTMLFormElement;
    form.submit();
  };

  const closeModal = () => {
    setIsOpen(false);
  };

  return (
    <>
      <div>
        <div className="pb-3">
          <Headings
            level={1}
            text={t('common.contactUs')}
            variant="semiBold"
            dataTestId="contactUsHeadingText"
          />
        </div>
        <form>
          <Label
            className="pb-1 "
            dataTestId="questionTypeDropdownLabel"
            htmlFor="questionTypeDropdown"
          >
            {t('contactUs.questiontype')}
          </Label>
          <span className="text-danger "> *</span>
          <div className="mb-3">
            <Select
              dataTestId="contactusQuestionTypeDropdown"
              className={'form-select'}
              name="questionTypeDropdown"
              onChange={handleQuestionChange}
              defaultValue=""
            >
              <option value="" disabled selected hidden>
                --
              </option>
              {questionTypes?.map((list: string) => (
                <option
                  value={list}
                  key={list}
                  data-testid={removeSpaceSlash(list)}
                >
                  {list}
                </option>
              ))}
              ;
            </Select>
          </div>
          <Label
            className="pb-1 "
            dataTestId="howContactedDropdownLabel"
            htmlFor="howContactedDropdown"
          >
            {t('contactUs.contacted')}
          </Label>
          <span className="text-danger "> *</span>
          <div className="mb-3">
            <Select
              dataTestId="contactusQuestionTypeDropdown"
              className={'form-select'}
              name="howContactedDropdown"
              onChange={handleQuestionChange}
              defaultValue=""
            >
              <option value="" disabled selected hidden>
                --
              </option>
              {contactOptions?.map((list: string) => (
                <option
                  value={list}
                  key={list}
                  data-testid={removeSpaceSlash(list)}
                >
                  {list}
                </option>
              ))}
              ;
            </Select>
          </div>
          <Label
            className="pb-1 "
            dataTestId="planTypeLabel"
            htmlFor="regPlanType"
          >
            {t('contactUs.yourQuestion')}
          </Label>
          <span className="text-danger "> *</span>
          <div className="mb-4 your-question">
            <TextArea
              rows={9}
              onChange={handleQuestionChange}
              className="form-input"
              name="yourQuestion"
            />
          </div>
          <div className="mb-3">
            <Button
              dataTestId={'contactSubmit'}
              variant="primary_btn"
              className="btn-md-width"
              disabled={
                selectedList.questionTypeDropdown === '' ||
                selectedList.howContactedDropdown === '' ||
                selectedList.yourQuestion === ''
              }
              type="submit"
              onClick={redirectToContact}
            >{t('common.submit')}</Button>
          </div>
        </form>
        <ModalOverlayWithoutClose
          isOpen={isOpen}
          overlayInfo={t('contactUs.overlayMessage')}
        >
          <div className="mb-3 pb-1 text-center">
            <CheckIconBig />
          </div>
          <div className="mb-3 pb-1">
            <Headings
              level={1}
              className="text-center"
              text={t('common.activationSuccess')}
              variant="semiBold"
              dataTestId="successMessage"
            />
          </div>
          <div data-testid="contactusModalTitle">
            <p
              className="mb-4 text-center"
              data-testid="contactusModalDescription"
            >
              {t('contactUs.successMessage')}
            </p>
            <div className="text-center">
              <Button
                dataTestId={'contactSubmit'}
                variant="primary_btn"
                onClick={closeModal}
              >{t('common.ok')}</Button>
            </div>
          </div>
        </ModalOverlayWithoutClose>
        <Paragraph text={t('contactUs.note')} variant="regular" data-testid="contactusFooterNote" />
      </div>
      <form action={ContactUSKeyValues.url} method="post" id="salesForce">
        <input type="hidden" name="orgid" value={ContactUSKeyValues.orgId} />
        <input type="hidden" name="orgin" id="origin" value="Web" />
        <input type='hidden' name='appName__c' value={appName} />
        <input
          type="hidden"
          id={ContactUSKeyValues?.memberIdKey}
          name={ContactUSKeyValues?.memberIdKey}
          value={memberId}
        />
        <input type="hidden" name="retURL" value={contactUsBaseURL} />
        <input
          type="hidden"
          name={ContactUSKeyValues?.questionTypeKey}
          id={ContactUSKeyValues?.questionTypeKey}
          value={selectedList.questionTypeDropdown}
        />
        <input
          type="hidden"
          name={ContactUSKeyValues?.contactOptionKey}
          id={ContactUSKeyValues?.contactOptionKey}
          value={selectedList.howContactedDropdown}
        />
        <input
          type="hidden"
          name="email"
          id="email"
          value={profileState?.email}
        />
        <input
          type="hidden"
          name="description"
          value={selectedList?.yourQuestion}
        />
        <input type="hidden" value="submit" />
      </form>
    </>
  );
};

export default ContactUs;